/**
 * First-touch marketing attribution.
 *
 * Captures utm_source/medium/campaign/content/term, gclid, and
 * document.referrer from the landing URL and persists them to localStorage
 * under one key. FIRST TOUCH WINS: if a record with a campaign is already
 * stored, later visits (e.g. the same person returning organically after
 * clicking an ad days earlier) never overwrite it — crediting the campaign
 * that originally acquired the visitor is the entire point of attribution.
 * The one exception is a record that holds nothing but a referrer: it carries
 * no campaign to protect, so a later visit that arrives WITH utm_* or a gclid
 * replaces it (otherwise a single organic hit would lock a visitor out of
 * paid attribution for good).
 *
 * captureAttribution() runs once per page load, client-side, from the root
 * layout's PageViewTracker (components/Analytics.tsx). getAttribution() reads
 * it back later, e.g. to attach to the Stripe checkout session's metadata.
 * resolveAttribution() is what a form uses to decide what to SEND: one visit's
 * set, never a mix, and nothing at all without consent.
 *
 * CONSENT: everything here is consent-based. Without the banner's 'full' value
 * nothing is read from or written to the storage key (§ 25 TDDDG) and a form
 * sends no utm_* or gclid. Accepting later captures the current page's URL
 * params at once (onConsentChange); declining or withdrawing removes the key.
 */

const STORAGE_KEY = 'sendvc_attribution';
// Written by components/layout/CookieConsent.tsx: 'full' = accepted,
// 'essential' = declined. Read by components/TrackingScripts.tsx the same way.
// onConsentChange() announces a change to the page with CONSENT_EVENT.
export const CONSENT_EVENT = 'sendvc:consent-change';
const CONSENT_KEY = 'cookie-consent';

// gclid is included alongside the utm_* params (not a UTM param itself) so
// Google Ads clicks stay attributable even on links someone forgot to tag.
export const CAPTURED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
] as const;

export type CapturedParam = (typeof CAPTURED_PARAMS)[number];

/**
 * A stored gclid older than this is not sent. Google Ads cannot credit a click
 * past its 90-day maximum conversion window, so an older id attributes nothing
 * and only adds an identifier we hold for no reason.
 */
export const GCLID_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  referrer?: string;
  /**
   * Epoch ms the record was written. Absent on records stored before this field
   * existed; those are treated as unexpired.
   */
  capturedAt?: number;
}

type Params = Partial<Record<CapturedParam, string>>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The captured params `get` yields as non-empty strings; anything else is dropped. */
function pickParams(get: (key: CapturedParam) => unknown): Params {
  const out: Params = {};
  for (const key of CAPTURED_PARAMS) {
    const value = get(key);
    if (typeof value === 'string' && value.trim()) out[key] = value.trim();
  }
  return out;
}

const hasAny = (o: object) => Object.keys(o).length > 0;

function gclidExpired(record: Record<string, unknown>, now: number): boolean {
  const at = record.capturedAt;
  return typeof at === 'number' && Number.isFinite(at) && now - at > GCLID_MAX_AGE_MS;
}

/**
 * Pure. True only for the cookie banner's "accept all" value. Anything else
 * (declined, never answered, a value this code does not know) is denied, which
 * is the same default TrackingScripts applies to Google's consent mode.
 */
export function isFullConsent(stored: unknown): boolean {
  return stored === 'full';
}

/** Reads the banner's choice. Storage that throws (Safari private mode) means no consent. */
export function hasFullConsent(): boolean {
  try {
    return isFullConsent(localStorage.getItem(CONSENT_KEY));
  } catch {
    return false;
  }
}

/**
 * Pure. The attribution a form should send, as ONE visit's set:
 *
 * - If the current URL carries any utm_* or gclid, ONLY the URL's set is used.
 *   The stored first-touch is not consulted, not even to fill a missing key —
 *   a utm_campaign from one visit next to a gclid from another would credit a
 *   click to a campaign it never came from.
 * - Otherwise ONLY the stored first-touch set is used. Its gclid is dropped
 *   once it is older than GCLID_MAX_AGE_MS (utm_* are campaign labels and do
 *   not expire). A record without capturedAt predates the field: unexpired.
 * - Without consent NOTHING is sent: {} (utm_* and gclid both). Campaign
 *   parameters of the current URL are processed only with the visitor's consent.
 */
export function resolveAttribution({
  search,
  stored,
  consentFull,
  now = Date.now(),
}: {
  /** window.location.search */
  search: string;
  /** Whatever the storage held; validated here, never trusted. */
  stored: unknown;
  consentFull: boolean;
  now?: number;
}): Params {
  if (!consentFull) return {};

  const params = new URLSearchParams(search);
  let picked = pickParams((key) => params.get(key));

  if (!hasAny(picked)) {
    const record = isRecord(stored) ? stored : {};
    picked = pickParams((key) => record[key]);
    if (gclidExpired(record, now)) delete picked.gclid;
  }

  return picked;
}

/**
 * Pure. The record captureAttribution() should write, or null to leave storage
 * alone. `existing` is whatever the storage held (null when nothing, or
 * unparsable — a corrupt value no longer blocks a fresh capture).
 *
 * ponytail: an existing record whose gclid has expired is still "a campaign" and
 * is kept; the URL-wins rule in resolveAttribution covers a visitor who comes
 * back through a fresh ad click and submits on that same page. Replace on
 * expiry too if returning-after-90-days paid visitors turn out to matter.
 */
export function nextAttribution({
  existing,
  search,
  referrer,
  now = Date.now(),
}: {
  existing: unknown;
  search: string;
  referrer?: string;
  now?: number;
}): Attribution | null {
  const params = new URLSearchParams(search);
  const fromUrl = pickParams((key) => params.get(key));

  if (isRecord(existing)) {
    const holdsCampaign = hasAny(pickParams((key) => existing[key]));
    if (holdsCampaign || !hasAny(fromUrl)) return null;
  }

  // One visit as a unit: a replaced referrer-only record does not lend its
  // referrer to the new campaign record.
  const record: Attribution = { ...fromUrl };
  if (referrer) record.referrer = referrer;

  // Nothing to capture (direct visit, no campaign params, no referrer) —
  // skip the write so a later visit that DOES carry UTMs can still land.
  if (!hasAny(record)) return null;

  record.capturedAt = now;
  return record;
}

function readStored(): unknown {
  if (!hasFullConsent()) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Safari private mode throws on localStorage access, and a corrupt/
    // foreign value at this key would throw on JSON.parse — either way,
    // attribution is best-effort, so treat it as "nothing captured".
    return null;
  }
}

/**
 * Reads the persisted first-touch attribution, or null if none was ever
 * captured (direct visitor, no marketing consent, localStorage unavailable, a
 * value that is not an object, or captureAttribution() hasn't run yet in this
 * session). An expired gclid (see GCLID_MAX_AGE_MS) is dropped from the result
 * so the checkout path honours the same 90-day limit as the lead form.
 */
export function getAttribution(): Attribution | null {
  const stored = readStored();
  if (!isRecord(stored)) return null;
  const out = { ...stored } as Attribution;
  if (gclidExpired(stored, Date.now())) delete out.gclid;
  return out;
}

/**
 * Captures UTM params + gclid + referrer from the current URL on first
 * landing. No-ops once a record that holds a campaign is stored — that's what
 * makes it first-touch (see nextAttribution for the one replaceable case).
 */
export function captureAttribution(): void {
  if (!hasFullConsent()) return;
  try {
    const next = nextAttribution({
      existing: readStored(),
      search: window.location.search,
      referrer: document.referrer,
    });
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Safari private mode throws on localStorage access — attribution is
    // best-effort and must never block page load or navigation over it.
  }
}

/** Removes the stored record. Never throws (Safari private mode). */
export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored if storage is unavailable.
  }
}

/**
 * Call right AFTER the banner's choice is written to storage: 'full' captures
 * the current page's landing params immediately (the visitor may have arrived
 * before answering the banner); anything else removes the stored record.
 */
export function onConsentChange(value: 'full' | 'essential'): void {
  if (isFullConsent(value)) captureAttribution();
  else clearAttribution();
  // Lets the Google tag loader (components/TrackingScripts.tsx) react without a reload.
  try {
    window.dispatchEvent(new Event(CONSENT_EVENT));
  } catch {
    // No window (tests/SSR): nothing to notify.
  }
}
